import { Container, Graphics, Text } from "pixi.js";
import {
  COLORS,
  GK,
  kitColorsFor,
  PLAYER,
  POSSESSION,
  type KitPattern,
  type Player,
} from "@golazo/shared";
import { lerp } from "../core/math.js";
import { LOOK, LOOK_FLOATS } from "../game/replay.js";
import { createSoftShadow } from "./textures.js";

interface PlayerView {
  player: Player;
  root: Container;
  chassis: Container;
  body: Graphics;
  core: Graphics;
  shell: Graphics;
  facingDot: Graphics;
  controlRing: Graphics;
  possessionRing: Graphics;
  possessionOrbit: Graphics;
  stateRing: Graphics;
  chargeArc: Graphics;
  /** Destello de "boton pulsado" mientras se mantiene el golpeo. */
  kickGlow: Graphics;
  numberLabel: Text;
  nickLabel: Text;
  pulse: number;
  phase: number;
  /** Velocidad del frame anterior, para detectar impactos. */
  prevVelX: number;
  prevVelY: number;
  /** Compresión por choque, 0..1, decae sola. */
  squash: number;
  /** Ángulo suavizado del chasis. */
  facingAngle: number;
  /** Barrido de brillo sobre el casco. */
  sheen: Graphics;
  sheenTimer: number;
  /** Portero: el brazo estirado. `null` en jugadores de campo. */
  diveArm: Graphics | null;
  /** Geometría del equipo, para dibujar fantasmas y chispas. */
  sides: number;
  shellRot: number;
  accent: number;
  /** Segundos hasta soltar el próximo fantasma de dash. */
  ghostTimer: number;
  /** Detección de flanco del trompicón para las chispas. */
  wasStumbling: boolean;
}

/** Fantasma de dash: silueta que se queda atrás y se apaga. */
interface Ghost {
  x: number;
  y: number;
  rot: number;
  life: number;
  sides: number;
  shellRot: number;
  radius: number;
  color: number;
}

/** Chispas de una entrada recibida. */
interface Spark {
  x: number;
  y: number;
  life: number;
  radius: number;
  color: number;
}

const GHOST_LIFE = 0.3;
const SPARK_LIFE = 0.34;

/**
 * Las franjas de la camiseta, en coordenadas del casco.
 *
 * Se dibuja sobre el cuadrado [-r, r] y luego se recorta con la silueta: es lo
 * que permite describir cada patron con dos lineas en vez de resolver a mano la
 * interseccion con un hexagono.
 */
function drawKitPattern(pattern: KitPattern, r: number, color: number): Graphics {
  const g = new Graphics();
  // Franja ancha a proposito: a este tamaño en pantalla, rayas finas se funden
  // en un tono medio y la camiseta pierde justo lo que la identifica.
  const band = r * 0.5;

  if (pattern === "stripes") {
    for (let x = -r; x < r; x += band * 2) g.rect(x, -r, band, r * 2);
  } else if (pattern === "hoops") {
    for (let y = -r; y < r; y += band * 2) g.rect(-r, y, r * 2, band);
  } else if (pattern === "halves") {
    g.rect(0, -r, r, r * 2);
  } else if (pattern === "sash") {
    // Banda cruzada de hombro a cadera, con el grosor de una franja y media.
    const w = band * 1.5;
    g.moveTo(-r, r * 0.1)
      .lineTo(r * 0.1, -r)
      .lineTo(r * 0.1 + w, -r)
      .lineTo(-r + w, r * 0.1)
      .closePath();
  } else if (pattern === "quarters") {
    // Dos cuartos opuestos: el tablero de toda la vida.
    g.rect(0, -r, r, r);
    g.rect(-r, 0, r, r);
  } else if (pattern === "chevron") {
    // Un galon en V sobre el pecho, apuntando hacia delante.
    const w = band * 0.9;
    g.moveTo(-r, -r * 0.55)
      .lineTo(r * 0.2, r * 0.05)
      .lineTo(-r, r * 0.65)
      .lineTo(-r, r * 0.65 - w * 1.4)
      .lineTo(r * 0.2 - w * 1.2, r * 0.05)
      .lineTo(-r, -r * 0.55 + w * 1.4)
      .closePath();
  } else if (pattern === "band") {
    // Una sola franja ancha por el centro, de arriba abajo.
    g.rect(-band * 0.6, -r, band * 1.2, r * 2);
  } else if (pattern === "cross") {
    // Cruz nordica: el brazo vertical desplazado hacia atras, como en las banderas.
    const w = band * 0.8;
    g.rect(-r * 0.35 - w / 2, -r, w, r * 2);
    g.rect(-r, -w / 2, r * 2, w);
  }

  g.fill({ color });
  return g;
}

/** Vertices of a regular polygon, first vertex at `rotation`. */
function polygon(sides: number, radius: number, rotation: number): number[] {
  const pts: number[] = [];
  for (let i = 0; i < sides; i += 1) {
    const a = rotation + (i / sides) * Math.PI * 2;
    pts.push(Math.cos(a) * radius, Math.sin(a) * radius);
  }
  return pts;
}

/** Cuanto dura el fogonazo de una pulsacion suelta de golpeo (s). */
const KICK_FLASH_TIME = 0.22;

export interface PlayersLayer {
  container: Container;
  /**
   * El boton de golpeo, tal cual lo tiene el mando AHORA.
   *
   * Se alimenta del input crudo y no de la simulacion a proposito. La carga
   * (`chargeSteer`) solo arranca si ademas llevas el balon, asi que pulsar sin
   * balon —que es la mitad de las veces que uno pulsa— no encendia nada y el
   * juego parecia sordo. Aqui `pressed` es el flanco: destella SIEMPRE, lleves
   * balon o no.
   */
  setKickSignal(held: boolean, pressed: boolean, playerId?: string): void;
  /**
   * El aspecto grabado mientras se ve una repeticion (`Simulation.replayLook`),
   * o `null`. Con el puesto se pinta lo que paso en la jugada —el aro al pulsar,
   * la carga, el casco al esprintar— y no lo que se pulsa AHORA.
   */
  setReplayLook(look: Float32Array | null): void;
  sync(alpha: number, ownerId: string | null, renderDt: number): void;
}

export function createPlayersLayer(
  players: readonly Player[],
  controlledId: string,
  /** Otros jugadores de ESTE ordenador (dos en el mismo teclado). */
  otherLocalIds: readonly string[] = [],
): PlayersLayer {
  const container = new Container();
  container.label = "players";

  const currentId = controlledId;
  /** Los que se llevan desde este ordenador: aro de control y señal de golpeo. */
  const localIds = new Set([controlledId, ...otherLocalIds]);
  let prevOwnerId: string | null = null;
  /** Boton de golpeo mantenido, del input crudo. */
  const kickHeld = new Map<string, boolean>();
  /** Segundos que le quedan al fogonazo de la ultima pulsacion. */
  const kickFlash = new Map<string, number>();
  let replayLook: Float32Array | null = null;

  // Capa de efectos por debajo de las fichas: fantasmas de dash y chispas.
  const fx = new Graphics();
  container.addChild(fx);
  const ghosts: Ghost[] = [];
  const sparks: Spark[] = [];

  const views = players.map((player) => {
    const view = createPlayerView(player);
    container.addChild(view.root);
    return view;
  });

  const applyControlled = (): void => {
    for (const view of views) {
      const on = localIds.has(view.player.id);
      view.controlRing.visible = on;
      view.chargeArc.visible = on;
    }
  };
  applyControlled();

  return {
    container,

    setKickSignal(held: boolean, pressed: boolean, playerId?: string): void {
      const id = playerId ?? currentId;
      kickHeld.set(id, held);
      // Se reinicia el fogonazo en cada flanco: martillear la tecla tiene que
      // dar un destello por golpe, no uno solo que se apaga a su ritmo.
      if (pressed) kickFlash.set(id, KICK_FLASH_TIME);
    },

    setReplayLook(look: Float32Array | null): void {
      replayLook = look;
    },

    sync(alpha: number, ownerId: string | null, renderDt: number): void {
      if (ownerId !== prevOwnerId && ownerId) {
        const view = views.find((v) => v.player.id === ownerId);
        if (view) view.pulse = 1;
      }
      prevOwnerId = ownerId;
      // Una vez por frame: si se restara dentro del bucle, el fogonazo duraria
      // once veces menos en 11v11 que en 2v2.
      for (const [id, left] of kickFlash) kickFlash.set(id, Math.max(0, left - renderDt));

      fx.clear();
      for (let i = ghosts.length - 1; i >= 0; i -= 1) {
        const g = ghosts[i]!;
        g.life -= renderDt;
        if (g.life <= 0) {
          ghosts.splice(i, 1);
          continue;
        }
        const t = g.life / GHOST_LIFE;
        const pts = polygon(g.sides, g.radius * (0.75 + t * 0.25), g.shellRot + g.rot);
        for (let k = 0; k < pts.length; k += 2) {
          pts[k]! += g.x;
          pts[k + 1]! += g.y;
        }
        fx.poly(pts).stroke({
          width: Math.max(1, g.radius * 0.12),
          color: g.color,
          alpha: t * 0.45,
        });
      }
      for (let i = sparks.length - 1; i >= 0; i -= 1) {
        const s = sparks[i]!;
        s.life -= renderDt;
        if (s.life <= 0) {
          sparks.splice(i, 1);
          continue;
        }
        const t = s.life / SPARK_LIFE;
        const ring = s.radius * (1.1 + (1 - t) * 1.1);
        fx.circle(s.x, s.y, ring).stroke({ width: Math.max(1, s.radius * 0.16), color: s.color, alpha: t * 0.6 });
        for (let k = 0; k < 6; k += 1) {
          const a = (k / 6) * Math.PI * 2 + s.radius;
          fx.moveTo(s.x + Math.cos(a) * ring * 0.85, s.y + Math.sin(a) * ring * 0.85)
            .lineTo(s.x + Math.cos(a) * ring * 1.25, s.y + Math.sin(a) * ring * 1.25)
            .stroke({ width: Math.max(1, s.radius * 0.12), color: 0xffffff, alpha: t * 0.55 });
        }
      }

      const look = replayLook;
      for (let vi = 0; vi < views.length; vi += 1) {
        const view = views[vi]!;
        /*
         * En la repeticion, todo lo que no es posicion sale de lo GRABADO: si
         * no, lo que pulsabas mientras la veias se encendia encima del clip.
         */
        const l = vi * LOOK_FLOATS;
        const burstTime = look ? look[l + LOOK.BURST]! : view.player.burstTime;
        const charge = look ? look[l + LOOK.CHARGE]! : view.player.charge;
        const chargeSteer = look ? look[l + LOOK.STEER]! > 0 : view.player.chargeSteer;
        const dashCooldown = look ? look[l + LOOK.DASH_CD]! : view.player.dashCooldown;
        const tackleCooldown = look ? look[l + LOOK.TACKLE_CD]! : view.player.tackleCooldown;
        const {
          player,
          root,
          chassis,
          body,
          core,
          shell,
          possessionRing,
          possessionOrbit,
          stateRing,
          chargeArc,
          kickGlow,
          numberLabel,
          nickLabel,
        } = view;
        const now = performance.now() * 0.001;
        root.x = lerp(player.prevPos.x, player.pos.x, alpha);
        root.y = lerp(player.prevPos.y, player.pos.y, alpha);
        numberLabel.text = String(player.shirtNumber);

        // El chasis apunta a donde mira el jugador, con giro suavizado por el
        // camino corto del ángulo (nunca da la vuelta larga al cruzar ±π).
        const targetAngle = Math.atan2(player.facing.y, player.facing.x) + Math.PI / 2;
        let delta = targetAngle - view.facingAngle;
        delta -= Math.PI * 2 * Math.floor((delta + Math.PI) / (Math.PI * 2));
        view.facingAngle += delta * Math.min(1, renderDt * 14);
        chassis.rotation = view.facingAngle;

        // Squash & stretch: se estira en la dirección de avance según la velocidad
        // y se comprime de golpe al chocar (cambio brusco de velocidad).
        const speed = Math.hypot(player.vel.x, player.vel.y);
        const impact = Math.hypot(player.vel.x - view.prevVelX, player.vel.y - view.prevVelY);
        view.prevVelX = player.vel.x;
        view.prevVelY = player.vel.y;
        if (impact > 60) view.squash = Math.min(1, view.squash + impact / 420);
        view.squash = Math.max(0, view.squash - renderDt * 4);

        const stretch = 1 + Math.min(0.14, speed / 2600) - view.squash * 0.18;
        chassis.scale.set(1 / stretch, stretch);

        if (player.nickname) {
          nickLabel.visible = true;
          // El recorte es solo para que quepa sobre el jugador: el dato
          // sigue entero para la tabla final.
          nickLabel.text =
            player.nickname.length > 12 ? `${player.nickname.slice(0, 11)}…` : player.nickname;
          nickLabel.y = -player.radius - Math.max(10, player.radius * 0.55);
        } else {
          nickLabel.visible = false;
        }

        // Brazo del portero: lo que hace visible la estirada. Sin esto la
        // capsula de alcance existiria en la fisica y no se veria en pantalla,
        // que es el peor sitio donde puede vivir una mecanica.
        if (view.diveArm) {
          const arm = view.diveArm;
          if (player.diveTime > 0) {
            const r = player.radius;
            const reach = r * (1 + GK.ARM_SCALE);
            const ax = player.diveDir.x;
            const ay = player.diveDir.y;
            // Se extiende de golpe y se recoge: el pico es al principio.
            const ext = Math.min(1, (GK.DIVE_TIME - player.diveTime) / 0.09 + 0.35);
            const tipX = ax * reach * ext;
            const tipY = ay * reach * ext;
            arm.visible = true;
            arm.clear();
            arm
              .moveTo(ax * r * 0.5, ay * r * 0.5)
              .lineTo(tipX, tipY)
              .stroke({ width: Math.max(3, r * 0.34), color: view.accent, alpha: 0.9, cap: "round" });
            arm.circle(tipX, tipY, Math.max(3, r * 0.3)).fill({ color: 0xffffff, alpha: 0.92 });
          } else if (arm.visible) {
            arm.visible = false;
          }
        }

        view.pulse = Math.max(0, view.pulse - renderDt * 4.5);
        /*
         * El salto del portero, en cenital: crece.
         *
         * Desde arriba, subir es acercarse a la camara. Un 22% de escala en
         * campana —sube y baja dentro del vuelo— basta para leerlo como un
         * salto, y no hace falta ninguna capa nueva. Sin esto la mecanica
         * existiria solo en la fisica, que es el peor sitio donde puede vivir.
         */
        let jumpScale = 1;
        if (player.jumpTime > 0) {
          const t = 1 - player.jumpTime / GK.JUMP_TIME;
          jumpScale = 1 + Math.sin(Math.PI * t) * 0.22;
        }
        const pulseScale = (1 + view.pulse * 0.12) * jumpScale;

        let alphaMul = 1;
        let wobble = 0;
        if (player.stumbleTime > 0) {
          alphaMul = 0.72;
          wobble = Math.sin(player.stumbleTime * 28) * 0.08;
        }

        const burstScale = burstTime > 0 ? 1.06 : 1;
        root.rotation = wobble;
        root.scale.set(pulseScale * burstScale);

        const hasBall = player.id === ownerId;
        const accent = view.accent;

        // El núcleo respira; se acelera al esprintar y late fuerte con balón.
        const beat = burstTime > 0 ? 9 : hasBall ? 6.5 : 3;
        core.alpha = (0.55 + Math.sin(now * beat + view.phase) * 0.25) * (hasBall ? 1.35 : 1);
        core.rotation = now * (player.side === "home" ? 0.9 : -0.6);
        // Estela fantasma mientras dura el impulso del dash.
        if (burstTime > 0) {
          view.ghostTimer -= renderDt;
          if (view.ghostTimer <= 0) {
            view.ghostTimer = 0.045;
            ghosts.push({
              x: root.x,
              y: root.y,
              rot: view.facingAngle,
              life: GHOST_LIFE,
              sides: view.sides,
              shellRot: view.shellRot,
              radius: player.radius,
              color: view.accent,
            });
          }
        } else {
          view.ghostTimer = 0;
        }

        // Chispas al recibir una entrada (flanco de subida del trompicón).
        const stumbling = player.stumbleTime > 0;
        if (stumbling && !view.wasStumbling) {
          sparks.push({
            x: root.x,
            y: root.y,
            life: SPARK_LIFE,
            radius: player.radius,
            color: view.accent,
          });
        }
        view.wasStumbling = stumbling;

        // Barrido de brillo: cada pocos segundos un destello recorre el casco.
        view.sheenTimer -= renderDt;
        if (view.sheenTimer <= -0.45) view.sheenTimer = 3 + view.phase;
        view.sheen.clear();
        if (view.sheenTimer < 0) {
          const t = -view.sheenTimer / 0.45;
          const a = -Math.PI / 2 + t * Math.PI * 2;
          view.sheen
            .arc(0, 0, player.radius * 0.95, a, a + 0.9)
            .stroke({ width: Math.max(1, player.radius * 0.16), color: 0xffffff, alpha: (1 - t) * 0.5 });
        }

        // El casco se enciende al esprintar y al cargar el golpeo.
        shell.alpha = Math.min(1, (burstTime > 0 ? 1 : 0.75) + charge * 0.3);
        shell.scale.set(1 + charge * 0.05);

        possessionRing.clear();
        possessionOrbit.clear();
        if (hasBall) {
          const breathe = 0.55 + Math.sin(now * 6) * 0.15;
          possessionRing
            .circle(0, 0, player.radius + 4)
            .stroke({ width: 2, color: accent, alpha: breathe * 0.55 })
            .circle(0, 0, player.radius + 7)
            .stroke({ width: 1.5, color: COLORS.CHARGE, alpha: breathe * 0.7 });

          // Satélites orbitales: marca única de posesión.
          const orbitR = player.radius + 7;
          for (let i = 0; i < 3; i += 1) {
            const a = now * 2.2 + (i / 3) * Math.PI * 2;
            possessionOrbit
              .circle(Math.cos(a) * orbitR, Math.sin(a) * orbitR, Math.max(1.2, player.radius * 0.13))
              .fill({ color: COLORS.CHARGE, alpha: 0.85 });
          }
        }

        stateRing.clear();
        if (player.trapTime > 0) {
          const t = player.trapTime / POSSESSION.TRAP_TIME;
          stateRing
            .circle(0, 0, player.radius + 2)
            .stroke({ width: 2, color: 0xffffff, alpha: t * 0.7 });
        } else if (dashCooldown > 0 && dashCooldown > PLAYER.DASH_COOLDOWN * 0.85) {
          stateRing
            .circle(0, 0, player.radius + 3)
            .stroke({ width: 2, color: COLORS.CONTROL_RING, alpha: 0.35 });
        }

        body.alpha = alphaMul;
        numberLabel.alpha = alphaMul;

        /*
         * "Te he oido": el disco se enciende al pulsar golpeo, como en HaxBall.
         *
         * Hay TRES motivos para encenderse, y hacen falta los tres:
         *
         *  - `mine && kickHeld`: el boton esta mantenido. Es el estado, y vale
         *    aunque no lleves el balon.
         *  - `mine && flashT > 0`: el flanco de la pulsacion, con un fogonazo
         *    que decae. Es lo que hace que un toque seco —o pulsar en el vacio,
         *    lejos del balon— tambien se vea. Sin esto el juego parecia sordo
         *    justo cuando mas se pulsa.
         *  - `chargeSteer`: cualquier jugador cargando de verdad. En online eso
         *    enseña la carga del rival, que es informacion legitima y en
         *    HaxBall se ve igual.
         *
         * Lo que NO se usa como unica señal es `chargeSteer`: solo se pone si
         * ademas eres el dueño del balon.
         */
        const mine = localIds.has(player.id);
        const flashT = mine
          ? look
            ? Math.max(0, 1 - look[l + LOOK.SINCE]! / KICK_FLASH_TIME)
            : (kickFlash.get(player.id) ?? 0) / KICK_FLASH_TIME
          : 0;
        const held = look ? look[l + LOOK.HELD]! > 0 : (kickHeld.get(player.id) ?? false);
        if (chargeSteer || (mine && (held || flashT > 0))) {
          const r = player.radius;
          // Latido rapido para que el aro no se lea como una decoracion fija.
          const pulseGlow = 0.78 + Math.sin(now * 18) * 0.22;
          const heat = Math.min(1, charge * 1.6);
          // El fogonazo abre el aro y lo sube de brillo: el ojo lee "ha pasado
          // algo AHORA" por el cambio, no por el color.
          const burst = flashT * flashT;
          kickGlow.visible = true;
          kickGlow.clear();
          kickGlow
            .circle(0, 0, r + 3 + burst * r * 0.5)
            .stroke({
              width: Math.max(2, r * (0.22 + burst * 0.16)),
              color: 0xffffff,
              alpha: Math.min(1, pulseGlow + burst * 0.4),
            })
            .circle(0, 0, r + 3 + r * 0.34 + burst * r * 0.7)
            .stroke({
              width: Math.max(1.5, r * 0.18),
              color: COLORS.CHARGE,
              alpha: Math.min(0.95, 0.2 + heat * 0.5 + burst * 0.55),
            });
        } else if (kickGlow.visible) {
          kickGlow.visible = false;
          kickGlow.clear();
        }

        chargeArc.clear();
        if (!localIds.has(player.id)) continue;

        if (charge > 0.01) {
          const start = -Math.PI / 2;
          chargeArc
            .arc(0, 0, player.radius + 5, start, start + Math.PI * 2 * charge)
            .stroke({ width: Math.max(2, player.radius * 0.35), color: COLORS.CHARGE, cap: "round" });
        } else if (dashCooldown > 0) {
          const ready = 1 - dashCooldown / PLAYER.DASH_COOLDOWN;
          const start = -Math.PI / 2;
          chargeArc
            .arc(0, 0, player.radius + 5, start, start + Math.PI * 2 * ready)
            .stroke({
              width: Math.max(2, player.radius * 0.28),
              color: COLORS.HUD_TEXT,
              alpha: 0.4,
              cap: "round",
            });
        } else if (tackleCooldown > 0) {
          const ready = 1 - tackleCooldown / PLAYER.TACKLE_COOLDOWN;
          const start = -Math.PI / 2;
          chargeArc
            .arc(0, 0, player.radius + 5, start, start + Math.PI * 2 * ready)
            .stroke({
              width: Math.max(2, player.radius * 0.28),
              color: COLORS.AWAY_ACCENT,
              alpha: 0.55,
              cap: "round",
            });
        }
      }
    },
  };
}

function createPlayerView(player: Player): PlayerView {
  const root = new Container();
  root.label = player.id;

  const isHome = player.side === "home";
  // La camiseta manda sobre la paleta global: `COLORS.HOME/AWAY` solo son el
  // padron de la equipacion por defecto.
  const kit = kitColorsFor(player.side, player.role === "gk");
  const bodyColor = kit.body;
  const accent = kit.accent;
  const r = player.radius;
  const stroke = Math.max(2, Math.round(r * 0.28));

  const possessionRing = new Graphics();
  root.addChild(possessionRing);

  const possessionOrbit = new Graphics();
  root.addChild(possessionOrbit);

  const controlRing = new Graphics()
    .poly(polygon(isHome ? 6 : 8, r + 6, isHome ? -Math.PI / 2 : Math.PI / (isHome ? 6 : 8)))
    .stroke({ width: Math.max(2, stroke), color: COLORS.CONTROL_RING });
  controlRing.visible = false;
  root.addChild(controlRing);

  const playerShadow = createSoftShadow(r * 0.9, r * 0.34);
  if (playerShadow) {
    playerShadow.y = r * 0.72;
    playerShadow.alpha = 0.34;   // 0,34 x 0,64 de la textura = la densidad de antes
    root.addChild(playerShadow);
  } else {
    root.addChild(
      new Graphics().ellipse(0, r * 0.72, r * 0.9, r * 0.34).fill({ color: 0x000000, alpha: 0.22 }),
    );
  }

  // Carcasa facetada: hexágono angular para local, octógono suave para visitante.
  // El radio del casco coincide con el del hitbox circular, así que la física no cambia.
  const sides = isHome ? 6 : 8;
  const shellRot = isHome ? -Math.PI / 2 : Math.PI / sides;

  // El chasis gira hacia donde mira el jugador y se estira/aplasta; las etiquetas
  // (dorsal, apodo) se quedan fuera para que sigan siempre legibles y horizontales.
  const chassis = new Container();
  root.addChild(chassis);

  const body = new Graphics()
    .poly(polygon(sides, r, shellRot))
    .fill(bodyColor)
    .stroke({ width: stroke, color: 0x11180f, alpha: 0.85 });
  chassis.addChild(body);

  /*
   * El patron de la camiseta (rayas, mitades, banda, aros).
   *
   * Se recorta con una mascara del propio casco para que las franjas terminen
   * en el borde de la camiseta y no en un rectangulo. La mascara SOLO se crea
   * cuando hace falta: con equipaciones lisas —que son las de por defecto— no
   * se paga ni un stencil, y son veintidos jugadores en pantalla.
   */
  if (kit.pattern !== "plain") {
    const patternMask = new Graphics().poly(polygon(sides, r, shellRot)).fill(0xffffff);
    chassis.addChild(patternMask);
    const patternLayer = drawKitPattern(kit.pattern, r, kit.secondary);
    patternLayer.mask = patternMask;
    chassis.addChild(patternLayer);
  }

  // Bisel: cara superior aclarada y sombra interior abajo, para dar volumen.
  chassis.addChild(
    new Graphics()
      .poly(polygon(sides, r * 0.86, shellRot))
      .fill({ color: 0x000000, alpha: 0.18 })
      .moveTo(-r * 0.62, -r * 0.42)
      .lineTo(0, -r * 0.9)
      .lineTo(r * 0.62, -r * 0.42)
      .stroke({ width: Math.max(1, r * 0.1), color: 0xffffff, alpha: 0.22 }),
  );

  // Núcleo de energía interno (plasma para local, eléctrico para visitante).
  const core = new Graphics();
  if (isHome) {
    core.poly(polygon(3, r * 0.62, -Math.PI / 2)).fill({ color: accent, alpha: 0.3 });
  } else {
    core.poly(polygon(4, r * 0.6, 0)).fill({ color: accent, alpha: 0.26 });
  }
  chassis.addChild(core);

  // Panel holográfico donde se integra el dorsal.
  chassis.addChild(
    new Graphics()
      .poly(polygon(sides, r * 0.78, shellRot))
      .stroke({ width: Math.max(1, r * 0.09), color: accent, alpha: 0.55 }),
  );

  const shell = new Graphics()
    .poly(polygon(sides, r * 0.95, shellRot))
    .stroke({ width: Math.max(1, r * 0.12), color: accent, alpha: 0.75 });
  chassis.addChild(shell);

  // Emblema geométrico mínimo de equipo, sobre el dorsal.
  const emblem = new Graphics();
  if (isHome) emblem.poly(polygon(3, r * 0.2, -Math.PI / 2));
  else emblem.poly(polygon(4, r * 0.18, 0));
  emblem.fill({ color: 0xffffff, alpha: 0.7 });
  emblem.y = -r * 0.58;
  chassis.addChild(emblem);

  if (player.role === "gk") {
    chassis.addChild(new Graphics().circle(0, 0, r * 0.55).stroke({ width: stroke + 1, color: accent }));
  }

  // El brazo cuelga de `root` y NO del chasis: apunta hacia donde se ha
  // lanzado el portero, que no tiene por que ser hacia donde mira.
  const diveArm = player.role === "gk" ? new Graphics() : null;
  if (diveArm) {
    diveArm.visible = false;
    root.addChild(diveArm);
  }

  // Visor: cuña que marca el frente del chasis (sustituye al punto de facing).
  const facingDot = new Graphics()
    .moveTo(-r * 0.3, -r * 0.7)
    .lineTo(0, -r * 1.02)
    .lineTo(r * 0.3, -r * 0.7)
    .fill({ color: accent, alpha: 0.95 });
  chassis.addChild(facingDot);

  const sheen = new Graphics();
  chassis.addChild(sheen);

  const stateRing = new Graphics();
  root.addChild(stateRing);

  // Tipografía por equipo: los locales llevan un dorsal condensado tipo militar,
  // los visitantes uno limpio y espaciado, más futurista.
  const fontSize = Math.max(9, Math.round(r * (isHome ? 1.05 : 0.86)));
  const numberLabel = new Text({
    text: String(player.shirtNumber),
    style: {
      fontFamily: isHome ? "Bebas Neue, Impact, sans-serif" : "DM Sans, system-ui, sans-serif",
      fontSize,
      fontWeight: "700",
      letterSpacing: isHome ? 0 : Math.max(0.5, r * 0.05),
      // La tinta la decide la equipacion: un dorsal blanco sobre camiseta
      // amarilla no se lee, y ahi se pierde de quien es la jugada.
      fill: kit.ink,
      stroke: { color: 0x0d160f, width: Math.max(2, Math.round(r * 0.18)) },
      align: "center",
    },
  });
  numberLabel.anchor.set(0.5, 0.52);
  root.addChild(numberLabel);

  const chargeArc = new Graphics();
  chargeArc.visible = false;
  root.addChild(chargeArc);

  /**
   * El destello de HaxBall.
   *
   * Alli el disco se enciende en blanco en el instante en que pulsas golpear, y
   * esa respuesta inmediata es media mecanica: sabes que el juego te ha oido
   * ANTES de saber que va a salir. Aqui faltaba — el arco de carga tarda un
   * pelin en verse y ademas cuenta otra cosa (cuanta potencia llevas), asi que
   * el flanco de "he pulsado" no lo daba nadie.
   *
   * Va en `root` y no en el chasis: el chasis gira y se aplasta con el cuerpo,
   * y un destello de mando no debe deformarse con lo que hace el jugador.
   */
  const kickGlow = new Graphics();
  kickGlow.visible = false;
  root.addChild(kickGlow);

  const nickLabel = new Text({
    text: "",
    style: {
      fontFamily: "DM Sans, system-ui, sans-serif",
      fontSize: Math.max(10, Math.round(r * 0.72)),
      fontWeight: "700",
      fill: 0xf2fff5,
      stroke: { color: 0x0d160f, width: 3 },
      align: "center",
    },
  });
  nickLabel.anchor.set(0.5, 1);
  nickLabel.y = -r - 10;
  nickLabel.visible = false;
  root.addChild(nickLabel);

  return {
    player,
    root,
    chassis,
    body,
    core,
    shell,
    facingDot,
    controlRing,
    possessionRing,
    possessionOrbit,
    stateRing,
    chargeArc,
    kickGlow,
    numberLabel,
    nickLabel,
    diveArm,
    pulse: 0,
    phase: (player.shirtNumber * 1.7) % (Math.PI * 2),
    prevVelX: 0,
    prevVelY: 0,
    squash: 0,
    facingAngle: Math.atan2(player.facing.y, player.facing.x) + Math.PI / 2,
    sheen,
    sheenTimer: 1 + ((player.shirtNumber * 0.9) % 3),
    sides,
    shellRot,
    accent,
    ghostTimer: 0,
    wasStumbling: false,
  };
}
